import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Mail, Key, Lock, ArrowLeft, CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

export default function PasswordReset({ onBack }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [success, setSuccess] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Step 1: Request OTP
  const requestOTP = async () => {
    if (!email || !email.includes('@')) return toast.error('Introduce un email valido');
    setLoading(true);
    try {
      await api.post('/password-reset/request-otp', { email });
      toast.success('Codigo enviado a tu correo');
      setStep(2);
      setCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error enviando codigo');
    } finally { setLoading(false); }
  };

  // Step 2: Verify OTP
  const verifyOTP = async () => {
    const code = otp.join('');
    if (code.length !== 6) return toast.error('Introduce el codigo completo');
    setLoading(true);
    try {
      const { data } = await api.post('/password-reset/verify-otp', { email, otp: code });
      setResetToken(data.resetToken);
      toast.success('Codigo verificado');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Codigo incorrecto');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  // Step 3: Set new password
  const resetPassword = async () => {
    if (newPassword.length < 8) return toast.error('Minimo 8 caracteres');
    if (newPassword !== confirmPassword) return toast.error('Las contrasenas no coinciden');
    setLoading(true);
    try {
      await api.post('/password-reset/reset-password', { email, resetToken, newPassword });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error actualizando contrasena');
    } finally { setLoading(false); }
  };

  // OTP input handlers
  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && otp.join('').length === 6) verifyOTP();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const resendOTP = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await api.post('/password-reset/request-otp', { email });
      toast.success('Nuevo codigo enviado');
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error reenviando');
    } finally { setLoading(false); }
  };

  // Success screen
  if (success) {
    return (
      <div className="pr-container">
        <div className="pr-card">
          <div className="pr-success-icon"><CheckCircle size={56} /></div>
          <h2>Contrasena actualizada</h2>
          <p className="pr-subtitle">Ya puedes iniciar sesion con tu nueva contrasena</p>
          <button className="btn btn-primary pr-btn-full" onClick={onBack}>
            Ir al login
          </button>
          <div className="pr-brand">BY ENERGESTICS</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pr-container">
      <div className="pr-card">
        {/* Header */}
        <div className="pr-header">
          <div className="pr-logo">&#9788; BITACORA SOLAR</div>
          <div className="pr-logo-sub">Plataforma de Gestion Solar</div>
        </div>

        {/* Step indicators */}
        <div className="pr-steps">
          {[1, 2, 3].map(s => (
            <div key={s} className={`pr-step-dot ${step >= s ? 'active' : ''} ${step > s ? 'done' : ''}`}>
              {step > s ? <CheckCircle size={14} /> : s}
            </div>
          ))}
        </div>

        {/* STEP 1: Email */}
        {step === 1 && (
          <div className="pr-step-content">
            <h2>Recuperar contrasena</h2>
            <p className="pr-subtitle">Introduce tu email y te enviaremos un codigo de verificacion</p>
            <div className="pr-input-group">
              <Mail size={18} className="pr-input-icon" />
              <input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && requestOTP()}
                autoFocus
                className="pr-input"
              />
            </div>
            <button className="btn btn-primary pr-btn-full" onClick={requestOTP} disabled={loading}>
              {loading ? <><Loader size={16} className="pr-spinner" /> Enviando...</> : 'Enviar codigo'}
            </button>
          </div>
        )}

        {/* STEP 2: OTP */}
        {step === 2 && (
          <div className="pr-step-content">
            <h2>Codigo de verificacion</h2>
            <p className="pr-subtitle">Introduce el codigo de 6 digitos enviado a <strong>{email}</strong></p>
            <div className="pr-otp-container" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  className="pr-otp-input"
                />
              ))}
            </div>
            <button className="btn btn-primary pr-btn-full" onClick={verifyOTP} disabled={loading || otp.join('').length !== 6}>
              {loading ? <><Loader size={16} className="pr-spinner" /> Verificando...</> : 'Verificar codigo'}
            </button>
            <div className="pr-resend">
              {cooldown > 0
                ? <span className="pr-cooldown">Reenviar en {cooldown}s</span>
                : <button className="pr-link" onClick={resendOTP}>Reenviar codigo</button>
              }
            </div>
          </div>
        )}

        {/* STEP 3: New password */}
        {step === 3 && (
          <div className="pr-step-content">
            <h2>Nueva contrasena</h2>
            <p className="pr-subtitle">Elige una contrasena segura (minimo 8 caracteres)</p>
            <div className="pr-input-group">
              <Lock size={18} className="pr-input-icon" />
              <input
                type="password"
                placeholder="Nueva contrasena"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoFocus
                className="pr-input"
              />
            </div>
            <div className="pr-input-group">
              <Key size={18} className="pr-input-icon" />
              <input
                type="password"
                placeholder="Confirmar contrasena"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && resetPassword()}
                className="pr-input"
              />
            </div>
            {newPassword.length > 0 && (
              <div className="pr-strength">
                <div className={`pr-strength-bar ${newPassword.length >= 8 ? (newPassword.length >= 12 ? 'strong' : 'medium') : 'weak'}`}
                     style={{ width: `${Math.min(100, (newPassword.length / 12) * 100)}%` }} />
                <span className="pr-strength-label">
                  {newPassword.length < 8 ? 'Muy corta' : newPassword.length < 12 ? 'Aceptable' : 'Fuerte'}
                </span>
              </div>
            )}
            <button className="btn btn-primary pr-btn-full" onClick={resetPassword} disabled={loading}>
              {loading ? <><Loader size={16} className="pr-spinner" /> Guardando...</> : 'Actualizar contrasena'}
            </button>
          </div>
        )}

        {/* Back button */}
        <button className="pr-back" onClick={step === 1 ? onBack : () => setStep(step - 1)}>
          <ArrowLeft size={14} /> {step === 1 ? 'Volver al login' : 'Atras'}
        </button>

        <div className="pr-brand">BY ENERGESTICS</div>
      </div>
    </div>
  );
}
